// Clip API client — talks to the server-side proxy (which holds the Clip key +
// secret). NEVER put Clip credentials here; the browser only sends the shared
// proxy bearer token.
//
// Clip model for reconciliation: a settlement (deposit) is paid net into the bank
// as a "GANANCIAS CLIP" SPEI. Each settlement breaks into its individual card
// sales (which we match 1:1 to our Clip card payments) plus the commission that
// Clip withheld. net = gross − commission, so the bank deposit won't equal our
// gross sales — the commission sub closes the gap.
import { API_TOKEN } from "./invoicing";
import { CATEGORY } from "./bankReconciliation";

export const CLIP_PROXY_URL = "https://api.gomoverent.com/v2/clip/proxy.php";

export async function clipRequest(endpoint, { method = "GET", query = null, body = null } = {}) {
  const res = await fetch(CLIP_PROXY_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${API_TOKEN}` },
    body: JSON.stringify({ endpoint, method, query, body }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.error || "Clip API error");
  return data.data;
}

// GET /settlements?from&to  (YYYY-MM-DD, ≤90 days back) → { settlements: [...] }
export async function getClipSettlements(from, to) {
  const data = await clipRequest("settlements", { query: { from, to } });
  return data?.settlements || [];
}

// GET /settlements/{id} → { settlement: { details: [{ date, payments: [...] }] } }
export async function getClipSettlementDetail(settlementReportId) {
  const data = await clipRequest(`settlements/${settlementReportId}`, { query: { page_size: 100 } });
  return data?.settlement || null;
}

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Turn a settlement-with-details into the sub-movements we attach under the bank
// "GANANCIAS CLIP" parent: one card-sale sub per payment + one commission sub.
export function clipSettlementToSubs(settlement) {
  const payments = (settlement.details || []).flatMap((d) =>
    (d.payments || []).map((p) => ({ ...p, _date: (p.payment_date || d.date || "").substring(0, 10) }))
  );
  const subs = payments.map((p) => {
    const last4 = String(p.card?.last4 ?? "").replace(/\D/g, "").slice(-4);
    return {
      source: "clip_api", date: p._date, amount: round2(p.amount), credit: true,
      card_last4: last4 || null,
      card_type: /cred/i.test(p.payment_method || "") ? "credit" : "debit",
      auth: p.receipt_no || null,
      concepto: `Venta Clip ${p.card?.brand || ""} ****${last4} ${p.receipt_no || ""}`.trim(),
      category: CATEGORY.CARD_SALE,
    };
  });
  const gross = round2(settlement.gross_amount);
  const net = round2(settlement.disbursed_net_amount);
  const commission = round2(gross - net);
  return { net, gross, commission, disbursementDate: (settlement.disbursement_date || "").substring(0, 10), subs };
}
